import { create } from 'zustand';
import { isNative } from '../native/platform';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallState {
  /** Running inside the installed app already (its own window, no browser chrome). */
  installed: boolean;
  /** The browser offered its install prompt and we hold it for the button. */
  canPrompt: boolean;
  prompt(): Promise<void>;
}

let deferred: BeforeInstallPromptEvent | null = null;

function standalone(): boolean {
  if (isNative || typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
}

export const usePwaInstall = create<InstallState>((set) => ({
  installed: standalone(),
  canPrompt: false,
  async prompt() {
    const e = deferred;
    if (!e) return;
    deferred = null;
    set({ canPrompt: false });
    await e.prompt();
    const { outcome } = await e.userChoice;
    if (outcome !== 'accepted') {
      deferred = e;
      set({ canPrompt: true });
    }
  },
}));

/** Chromium fires beforeinstallprompt when the page qualifies; Firefox and Safari never do, so Settings explains the menu instead. */
export function watchInstall(): void {
  if (isNative || typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    usePwaInstall.setState({ canPrompt: true });
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    usePwaInstall.setState({ installed: true, canPrompt: false });
  });
}
